import React from 'react';
import styled, { css } from 'styled-components';
import { ScrollConsumer } from '@stickyroll/context';
import { assert, scrollTo } from '@stickyroll/utils';

/**
 * @type {StyledComponent<"div", {}>}
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const StyledMarker = styled.div `
	position: absolute;
	z-index: 1;
	top: calc((var(--pager-size) / 2) + var(--pager-gap));
	left: calc(0.5rem + (var(--pager-size) - var(--marker-width)) / 2);
	width: var(--marker-width);
	background: var(--marker-color);
	visibility: visible;

	&::before,
	&::after {
		position: absolute;
		content: "";
		display: block;
		height: calc(var(--marker-width) / 2);
		width: var(--marker-width);
		left: 0;
		background: inherit;
	}

	&::before {
		bottom: 100%;
		border-radius: calc(var(--marker-width) / 2) calc(var(--marker-width) / 2) 0 0;
	}

	&::after {
		top: 100%;
		border-radius: 0 0 calc(var(--marker-width) / 2) calc(var(--marker-width) / 2);
	}
`;
/**
 * @type {StyledComponent<"nav", {}>}
 * @param {IMarkerProps} props
 * @param {number} props.page
 * @param {number} props.progress
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const Marker = props => {
    return (React.createElement(StyledMarker, { style: {
            height: `calc(${props.progress} * (var(--pager-gap) * 2 + var(--pager-size)) + ${props.page} * (var(--pager-gap) * 2 + var(--pager-size)))`
        } }));
};

/**
 * @type {StyledComponent<"a", {}, PagerProps>}
 * @param {PagerProps} props
 * @param {boolean} [props.active]
 * @param {boolean} [props.selected]
 * @return {React.ReactHTMLElement<HTMLAnchorElement>}
 */
const Pager = styled.a `
	position: relative;
	z-index: 2;
	height: var(--pager-size);
	width: var(--pager-size);
	margin: var(--pager-gap) 0.5rem;
	visibility: visible;
	border-radius: 50%;
	display: flex;
	align-content: center;
	align-items: center;
	justify-content: center;
	text-align: center;
	color: currentColor;
	text-decoration: none;
	background-clip: content-box;

	${(props) => css `
		color: var(--color);
		background-color: ${props.selected ? "var(--pager-color-active)" : "var(--pager-color)"};
		border: var(--stroke-width) solid ${props.active ? "var(--marker-color)" : "transparent"};

		&:hover,
		&:focus {
			background-color: var(--pager-color-active);
			border-color: var(--marker-color);
		}
		&:focus {
			box-shadow: 0 0 0 2px var(--marker-color);
		}
	`};
`;
/**
 * @type {StyledComponent<"nav", {}>}
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const StyledPagers = styled.nav `
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	align-content: center;
	justify-content: center;
	margin: calc(var(--pager-gap) * -1) -0.5rem;
`;
/**
 * @type {StyledComponent<"nav", {}>}
 * @param {IPagerWrapperProps} props
 * @param {ITheme} [props.theme]
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const PagerWrapper = styled.div `
	${(props) => {
    return css `
		--marker-color: ${props.theme.markerColor};
		--marker-width: ${props.theme.markerWidth};
		--pager-background-color: ${props.theme.pagerBackgroundColor};
		--pager-color: ${props.theme.pagerColor};
		--pager-color-active: ${props.theme.pagerColorActive};
		--pager-gap: ${props.theme.pagerGap};
		--pager-size: ${props.theme.pagerSize};
		--stroke-width: ${props.theme.strokeWidth};
		${props.position}: 0;
	`;
}};

	position: absolute;
	z-index: 2;
	top: 50%;
	margin: 0 0.5rem;
	transform: translateY(-50%);
	background-color: var(--pager-background-color);
	border-radius: calc(var(--pager-size) / 2);
`;
PagerWrapper.defaultProps = {
    position: "left"
};
/**
 * @type {StyledComponent<"svg", {}>}
 * @return {React.ReactSVGElement<SVGSVGElement>}
 */
const Icon = styled.svg.attrs({
    viewBox: "0 0 24 24"
}) `
	width: 1.5rem;
	height: 1.5rem;
	fill: currentColor;
`;
/**
 * @type {React.FunctionComponent<IPagerBaseProps>}
 * @param {ISkipBaseProps} props
 * @param {number} props.page
 * @param {number} props.pages
 * @param {string} props.prefix
 * @param {number} props.progress
 * @param {boolean} [props.showLabels]
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const PagerBase = props => {
    const handleClick = (e) => {
        e.preventDefault();
        const target = e.target;
        const id = target.getAttribute("href").replace(/^#/, "");
        scrollTo(id, target);
    };
    const glue = props.prefix === "" ? "" : "/";
    return (React.createElement(PagerWrapper, { theme: props.theme, position: props.position },
        React.createElement(StyledPagers, null,
            React.createElement(Marker, { progress: props.progress, page: props.pageIndex }),
            Array(props.pages)
                .fill(Boolean)
                .map((x, i) => {
                const id = `${props.prefix}${glue}${i + 1}`;
                return (React.createElement(Pager, { key: id, active: i <= props.pageIndex, selected: i === props.pageIndex && props.progress < 1, href: `#${id}`, onClick: handleClick }, props.showLabels && i + 1));
            }),
            React.createElement(Pager, { href: `#${props.prefix}${glue}${props.pages + 1}`, selected: props.page === props.pages && props.progress === 1, active: props.page === props.pages && props.progress === 1, onClick: handleClick }, props.showLabels && (React.createElement(Icon, null,
                React.createElement("path", { d: "M4,5V19L11,12M18,5V19H20V5M11,5V19L18,12" })))))));
};
/**
 * @type {React.FunctionComponent<IPagersProps>}
 * @param {ISkipBaseProps} props
 * @param {number} props.page
 * @param {number} props.pages
 * @param {string} props.prefix
 * @param {number} props.progress
 * @param {boolean} [props.showLabels]
 * @param {boolean} [props.useContext]
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const RawPagers = props => {
    if (props.useContext) {
        return (React.createElement(ScrollConsumer, null, context => (React.createElement(PagerBase, { page: context.page, pageIndex: context.pageIndex, pages: context.pages, position: props.position, prefix: context.anchors, progress: context.progress, showLabels: props.showLabels, theme: props.theme }))));
    }
    assert(props.page, "number");
    assert(props.pageIndex, "number");
    assert(props.pages, "number");
    assert(props.progress, "number");
    assert(props.prefix, "string");
    return (React.createElement(PagerBase, { page: props.page, pageIndex: props.pageIndex, pages: props.pages, position: props.position, prefix: props.prefix, progress: props.progress, showLabels: props.showLabels, theme: props.theme }));
};
RawPagers.defaultProps = {
    position: "left"
};
const Pagers = styled(RawPagers) ``;

/**
 * @type {StyledComponent<"a", {}>}
 * @return {React.ReactHTMLElement<HTMLAnchorElement>}
 */
const SkipLink = styled.a `
	position: absolute;
	bottom: 0;
	right: 0;
	padding: 0.5rem 1rem;
	color: currentColor;
	font-size: 1rem;
	line-height: 1.5rem;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;
/**
 * @type {React.FunctionComponent<ISkipBaseProps>}
 * @param {ISkipBaseProps} props
 * @param {string} props.prefix
 * @return {React.ReactHTMLElement<HTMLAnchorElement>}
 */
const SkipBase = props => {
    const glue = props.prefix === "" ? "" : "/";
    const handleClick = (e) => {
        e.preventDefault();
        scrollTo(`${props.prefix}${glue}skip`, e.target, {
            noFocus: true,
            noHash: true
        });
    };
    return (React.createElement(SkipLink, { href: `#${props.prefix}${glue}skip`, onClick: handleClick }, "Skip"));
};
/**
 * @type {React.FunctionComponent<IPagersProps>}
 * @param {ISkipBaseProps} props
 * @param {string} [props.prefix]
 * @param {boolean} [props.useContext]
 * @return {React.ReactHTMLElement<HTMLElement>}
 */
const Skip = props => {
    if (props.useContext) {
        return React.createElement(ScrollConsumer, null, context => React.createElement(SkipBase, { prefix: context.anchors }));
    }
    assert(props.prefix, "string");
    return React.createElement(SkipBase, { prefix: props.prefix });
};

export { Icon, Marker, Pager, PagerBase, Pagers, RawPagers, Skip, SkipBase, SkipLink, StyledMarker };
